import clsx from 'clsx';
import { useMemo, useState } from 'react';

import { Icon, IconButton, Menu, MenuItem } from '@/ui';

import type { FC } from 'react';

export interface ScheduleMember {
  id: string;
  fullName: string;
}

export interface MembersSidebarProps {
  adminCoachId?: string;
  /**
   * MEN-5473/MEN-5635: the signed-in coach's own id. Without `adminCoachId` it
   * gives the coach the Ops menu layout: "Book" opens the coach's own
   * on-behalf booking page, the copy icon copies the member's booking link.
   */
  bookingLinkCoachId?: string;
  members: ScheduleMember[];
  /**
   * Seeds the uncontrolled search box. Storybook uses it to render the
   * search-results and empty-search states deterministically instead of
   * driving keystrokes from a `play` function.
   */
  initialSearch?: string;
  selectedMemberId: string | null;
  onSelectMember: (memberId: string) => void;
  onSeeAll: () => void;
}

type BookingRoute =
  'book-biweekly' | 'book-chemistry-call' | 'book-single-session';

const BOOKING_ACTIONS = [
  { label: 'Chem Call', route: 'book-chemistry-call' },
  { label: 'Single Session', route: 'book-single-session' },
  { label: 'Bi-Weekly', route: 'book-biweekly' },
] as const satisfies readonly { label: string; route: BookingRoute }[];

// MEN-5473: the member-facing booking page for `coachId`, bound to `memberId`.
const memberBookingPath = (
  route: BookingRoute,
  coachId: string,
  memberId: string,
) =>
  `/m/${route}/${encodeURIComponent(coachId)}?member=${encodeURIComponent(memberId)}`;

// The on-behalf booking page the "Book" item opens. Ops names the coach in the
// URL; a coach books on their own schedule, so their id comes from the session
// (MEN-5635). Moments enforces both: a non-admin must be the pair's coach.
const onBehalfBookingPath = (
  route: BookingRoute,
  memberId: string,
  adminCoachId: string | undefined,
) =>
  adminCoachId
    ? `/coach/manage/coaches/${encodeURIComponent(adminCoachId)}/members/${encodeURIComponent(memberId)}/${route}`
    : `/coach/schedule/members/${encodeURIComponent(memberId)}/${route}`;

// Members list + search. Clicking a member filters the schedule to that member
// (List view only); "Show All" clears the filter. Full data wiring in Phase 3f.
const MembersSidebar: FC<MembersSidebarProps> = ({
  adminCoachId,
  bookingLinkCoachId,
  initialSearch = '',
  members,
  onSeeAll,
  onSelectMember,
  selectedMemberId,
}) => {
  const [search, setSearch] = useState(initialSearch);
  const [searchResetKey, setSearchResetKey] = useState(0);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [clipboardFeedback, setClipboardFeedback] = useState<
    'failure' | 'success' | null
  >(null);
  const bookingCoachId = adminCoachId ?? bookingLinkCoachId;
  const hasBookingMenu = Boolean(bookingCoachId);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return members;
    return members.filter((member) =>
      member.fullName.toLowerCase().includes(term),
    );
  }, [members, search]);

  const handleSeeAll = () => {
    setMenuAnchor(null);
    setClipboardFeedback(null);
    setSearch('');
    setSearchResetKey((key) => key + 1);
    onSeeAll();
  };

  // MEN-5473: the copied link is the member's own booking page, bound to the
  // selected member with `?member=`. The staff route the "Book" item opens
  // is unreachable for a member, and an unbound member route would book for
  // whoever opens it; the booking pages refuse a link bound to someone else.
  const copyBookingLink = async (path: string) => {
    try {
      const link = new URL(path, window.location.origin).toString();
      await navigator.clipboard.writeText(link);
      setClipboardFeedback('success');
    } catch {
      setClipboardFeedback('failure');
    }
  };

  return (
    <aside
      className="h-full min-h-0 w-[231px] shrink-0 overflow-y-auto border-0 border-r border-solid border-cement-200 px-[15px] py-[18px] max-md:max-h-52 max-md:w-full max-md:border-b max-md:border-r-0"
      data-testid="schedule-members-sidebar"
    >
      <div className="mb-2 flex h-8 items-start justify-between">
        <h2 className="m-0 text-base font-semibold leading-6 tracking-[0.15px] text-cement-500">
          Members
        </h2>
        {selectedMemberId && (
          <button
            className="appearance-none border-0 bg-transparent p-0 text-xs font-medium leading-6 text-black underline"
            type="button"
            onClick={handleSeeAll}
          >
            Show All
          </button>
        )}
      </div>
      <div className="relative h-[25px] w-[205px] rounded-[15px] border border-solid border-cement-200 bg-white">
        <input
          key={searchResetKey}
          aria-label="Search members"
          className="size-full appearance-none rounded-[15px] border-0 bg-transparent pl-6 pr-7 text-[9px] font-medium leading-[13px] text-cement-500 outline-none placeholder:text-cement-300"
          placeholder="Search Members"
          // MEN-4239: `type="search"` paints WebKit's own square cancel
          // glyph on top of the design's `close` affordance below. Plain
          // text keeps one clear control, which stays keyboard reachable.
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <Icon className="absolute left-2.5 top-2 text-[10px] text-cement-300">
          search
        </Icon>
        {search ? (
          <button
            aria-label="Clear member search"
            // Preflight is off, so a bare <button> keeps the UA border and
            // grey fill — MEN-4239's "extra boxy X". Reset it explicitly.
            className="absolute right-2 top-1.5 cursor-pointer appearance-none border-0 bg-transparent p-0 leading-none text-cement-300"
            type="button"
            onClick={() => {
              setSearch('');
              setSearchResetKey((key) => key + 1);
            }}
          >
            <Icon className="text-[10px]">close</Icon>
          </button>
        ) : null}
      </div>
      {/* Preflight is off, so the UA list bullet and 40px indent survive
          unless they are cleared here; Figma aligns names with the heading. */}
      <ul className="mb-0 mt-2.5 list-none space-y-2.5 p-0">
        {filtered.map((member) => (
          <li key={member.id} className="flex items-center">
            <button
              aria-pressed={selectedMemberId === member.id}
              className={clsx(
                'min-w-0 flex-1 appearance-none truncate whitespace-nowrap rounded-full border-0 px-[15px] py-1.5 text-left text-xs leading-[18px] tracking-[0.15px]',
                selectedMemberId === member.id
                  ? 'bg-sage-200 font-medium'
                  : 'bg-transparent',
              )}
              type="button"
              onClick={() => {
                setMenuAnchor(null);
                setClipboardFeedback(null);
                onSelectMember(member.id);
              }}
            >
              {member.fullName}
            </button>
            {hasBookingMenu && (
              <IconButton
                aria-label={`Booking options for ${member.fullName}`}
                className="size-6 p-0"
                disabled={selectedMemberId !== member.id}
                onClick={(event) => setMenuAnchor(event.currentTarget)}
              >
                <Icon>more_horiz</Icon>
              </IconButton>
            )}
          </li>
        ))}
      </ul>
      {bookingCoachId && selectedMemberId ? (
        <Menu
          anchorEl={menuAnchor}
          MenuListProps={{
            sx: {
              alignItems: 'center',
              display: 'grid',
              gridTemplateColumns: '1fr auto',
            },
          }}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
        >
          {BOOKING_ACTIONS.flatMap(({ label, route }) => [
            <MenuItem
              key={`${route}-open`}
              {...{
                component: 'a' as const,
                href: onBehalfBookingPath(
                  route,
                  selectedMemberId,
                  adminCoachId,
                ),
              }}
            >
              Book {label}
            </MenuItem>,
            <MenuItem
              key={`${route}-copy`}
              aria-label={`Copy ${label} link`}
              className="mr-1 justify-center"
              onClick={() => {
                setMenuAnchor(null);
                copyBookingLink(
                  memberBookingPath(route, bookingCoachId, selectedMemberId),
                ).catch(() => undefined);
              }}
            >
              <Icon>content_copy</Icon>
            </MenuItem>,
          ])}
        </Menu>
      ) : null}
      {clipboardFeedback === 'success' ? (
        <span role="status">Link copied</span>
      ) : null}
      {clipboardFeedback === 'failure' ? (
        <span className="text-red-600" role="alert">
          Could not copy the link. Please try again.
        </span>
      ) : null}
    </aside>
  );
};

export default MembersSidebar;
